import React, { useState, useCallback, useEffect } from 'react';
import KPICard from '../../components/Dashboard/KPICard/KPICard';
import LineChart from '../../components/Dashboard/LineChart/LineChart';
import StackedBarChart from '../../components/Dashboard/StackedBarChart/StackedBarChart';
import BarChart from '../../components/Dashboard/BarChart/BarChart';
import { fetchApi } from '../../../helpers/fetch';
import './Admin.scss';

const Revenue = () => {
    const [dateRange, setDateRange] = useState('7days');
    const [loading, setLoading] = useState(false);
    
    // Default/initial data structure
    const [revenueData, setRevenueData] = useState({
        revenue: 0,
        revenueTrend: [],
        arpu: 0,
        arpuTrend: [],
        arppu: 0,
        arppuTrend: [],
        arpc: 0,
        arpcTrend: [],
        arpmc: 0,
        arpmcTrend: [],
        gmv: 0,
        gmvTrend: [],
        ltv: 0,
        ltvTrend: [],
        totalContributions: 0,
        totalContributionsTrend: [],
        totalPurchases: 0,
        totalPurchasesTrend: [],
        prevTotalContributions: 0,
        prevTotalPurchases: 0,
        gmvBySource: [],
        gmvByCategory: [],
        avgContributions: {
            oneTime: 0,
            monthly: 0,
            physical: 0,
            digital: 0
        },
        avgTimes: {
            contributeRecurring: 0,
            contributeOnce: 0,
            physicalPurchase: 0,
            digitalPurchase: 0
        }
    });

    const formatCurrency = useCallback((amount) => {
        return `€${(amount / 100).toFixed(2)}`;
    }, []);

    const formatTime = useCallback((hours) => {
        if (hours < 24) {
            return `${hours}h`;
        }
        const days = Math.floor(hours / 24);
        return `${days} day${days > 1 ? 's' : ''}`;
    }, []);

    // Helper function to extract trend data array
    // Handles: array of numbers, array of objects with {date, value}, or array of objects with just {value}
    const extractTrendData = useCallback((trendArray) => {
        if (!trendArray || !Array.isArray(trendArray) || trendArray.length === 0) {
            return undefined;
        }
        
        const trendData = trendArray.map(item => {
            if (typeof item === 'number') {
                return item;
            }
            if (item && typeof item === 'object') {
                // Handle {date: "...", value: 123} or {value: 123}
                return item.value !== undefined ? item.value : item;
            }
            return Number(item) || 0;
        }).filter(val => !isNaN(val) && val !== null && val !== undefined);
        
        return trendData.length > 0 ? trendData : undefined;
    }, []);

    // Helper function to extract value and trend from API response
    // Handles both formats: { value, trend, trendLabel, absoluteChange, trendData } or simple value
    const extractValueAndTrend = useCallback((data) => {
        if (data && typeof data === 'object' && 'value' in data) {
            // New format with trend data
            const trendValue = data.trend ? parseFloat(data.trend.replace(/[+%]/g, '')) : null;
            return {
                value: data.value,
                trend: trendValue,
                absoluteChange: data.absoluteChange !== undefined ? data.absoluteChange : null,
                trendLabel: data.trendLabel || 'since last week',
                trendData: data.trendData || null
            };
        }
        // Old format or simple value
        return {
            value: data || 0,
            trend: null,
            absoluteChange: null,
            trendLabel: 'since last week',
            trendData: null
        };
    }, []);

    const fetchRevenueData = useCallback(async () => {
        try {
            setLoading(true);
            
            const response = await fetchApi(`/admin/revenue?period=${dateRange}`);
            
            console.log('/admin/revenue response:', response);
            
            if (response.success && response.data) {
                console.log('Revenue data keys:', Object.keys(response.data));
                console.log('ARPU object:', response.data.arpu);
                console.log('ARPU trendData:', response.data.arpu?.trendData);
                setRevenueData(response.data);
            }
        } catch (error) {
            console.error('Error fetching revenue data:', error);
        } finally {
            setLoading(false);
        }
    }, [dateRange]);

    useEffect(() => {
        fetchRevenueData();
    }, [fetchRevenueData]);

    const revenueTrendData = revenueData.revenueTrend || [];
    const gmvBySourceData = revenueData.gmvBySource || [];
    const gmvByCategoryData = revenueData.gmvByCategory || [];

    return (
        <div className="revenue-dashboard">
            <div className="dashboard-header">
                <div className="header-controls">
                    <select 
                        value={dateRange} 
                        onChange={(e) => setDateRange(e.target.value)}
                        className="date-range-select"
                    >
                        <option value="7days">Last 7 days</option>
                        <option value="30days">Last 30 days</option>
                        <option value="90days">Last 90 days</option>
                        <option value="1year">Last year</option>
                    </select>
                    <button className="export-btn">Export</button>
                </div>
            </div>

            {/* Revenue Section */}
            <div className="dashboard-section">
                <div className="section-header">
                    <h2>Revenue</h2>
                </div>
                
                <div className="revenue-main">
                    <div className="revenue-kpi-chart">
                        {(() => {
                            const revenue = extractValueAndTrend(revenueData.revenue);
                            // Check if trendData is in the revenue object, otherwise fall back to revenueTrend array
                            const trendData = revenue.trendData 
                                ? extractTrendData(revenue.trendData) 
                                : extractTrendData(revenueData.revenueTrend);
                            return (
                                <KPICard
                                    title="Revenue"
                                    value={formatCurrency(revenue.value)}
                                    trend={revenue.trend}
                                    trendLabel={revenue.trendLabel}
                                    trendData={trendData}
                                    absoluteChange={revenue.absoluteChange}
                                    formatAbsoluteChange={formatCurrency}
                                    color={revenue.trend && revenue.trend >= 0 ? "#4caf50" : "#FF3232"}
                                    loading={loading}
                                />
                            );
                        })()}
                        <div className="chart-container">
                            <LineChart
                                data={revenueTrendData}
                                xAxisKey="date"
                                lines={[{ key: 'value', color: '#FF3232', name: 'Revenue' }]}
                                height={250}
                                formatTooltip={formatCurrency}
                                loading={loading}
                            />
                        </div>
                    </div>

                    <div className="revenue-kpis-grid">
                        {(() => {
                            const arpu = extractValueAndTrend(revenueData.arpu);
                            const trendData = arpu.trendData ? extractTrendData(arpu.trendData) : undefined;
                            return (
                                <KPICard
                                    title="ARPU"
                                    subtitle="Average Revenue Per User"
                                    value={formatCurrency(arpu.value)}
                                    trend={arpu.trend}
                                    trendLabel={arpu.trendLabel}
                                    trendData={trendData}
                                    absoluteChange={arpu.absoluteChange}
                                    formatAbsoluteChange={formatCurrency}
                                    color={arpu.trend && arpu.trend >= 0 ? "#4caf50" : "#f44336"}
                                    loading={loading}
                                />
                            );
                        })()}
                        {(() => {
                            const arppu = extractValueAndTrend(revenueData.arppu);
                            const trendData = arppu.trendData ? extractTrendData(arppu.trendData) : undefined;
                            return (
                                <KPICard
                                    title="ARPPU"
                                    subtitle="Average Revenue Per Paying User"
                                    value={formatCurrency(arppu.value)}
                                    trend={arppu.trend}
                                    trendLabel={arppu.trendLabel}
                                    trendData={trendData}
                                    absoluteChange={arppu.absoluteChange}
                                    formatAbsoluteChange={formatCurrency}
                                    color={arppu.trend && arppu.trend >= 0 ? "#4caf50" : "#f44336"}
                                    loading={loading}
                                />
                            );
                        })()}
                        {(() => {
                            const arpc = extractValueAndTrend(revenueData.arpc);
                            const trendData = arpc.trendData ? extractTrendData(arpc.trendData) : undefined;
                            return (
                                <KPICard
                                    title="ARPC"
                                    subtitle="Average Revenue Per Customer"
                                    value={formatCurrency(arpc.value)}
                                    trend={arpc.trend}
                                    trendLabel={arpc.trendLabel}
                                    trendData={trendData}
                                    absoluteChange={arpc.absoluteChange}
                                    formatAbsoluteChange={formatCurrency}
                                    color={arpc.trend && arpc.trend >= 0 ? "#4caf50" : "#f44336"}
                                    loading={loading}
                                />
                            );
                        })()}
                        {(() => {
                            const arpmc = extractValueAndTrend(revenueData.arpmc);
                            const trendData = arpmc.trendData ? extractTrendData(arpmc.trendData) : undefined;
                            return (
                                <KPICard
                                    title="ARPMC"
                                    subtitle="Average Revenue Per Monthly Customer"
                                    value={formatCurrency(arpmc.value)}
                                    trend={arpmc.trend}
                                    trendLabel={arpmc.trendLabel}
                                    trendData={trendData}
                                    absoluteChange={arpmc.absoluteChange}
                                    formatAbsoluteChange={formatCurrency}
                                    color={arpmc.trend && arpmc.trend >= 0 ? "#4caf50" : "#f44336"}
                                    loading={loading}
                                />
                            );
                        })()}
                    </div>
                </div>
            </div>

            {/* GMV Section */}
            <div className="dashboard-section">
                <div className="section-header">
                    <h2>GMV (Gross Merchandise Value)</h2>
                </div>

                <div className="gmv-kpis-grid">
                    {(() => {
                        const gmv = extractValueAndTrend(revenueData.gmv);
                        const trendData = gmv.trendData ? extractTrendData(gmv.trendData) : undefined;
                        return (
                            <KPICard
                                title="GMV"
                                subtitle="Gross Merchandise Value"
                                value={formatCurrency(gmv.value)}
                                trend={gmv.trend}
                                trendLabel={gmv.trendLabel}
                                trendData={trendData}
                                absoluteChange={gmv.absoluteChange}
                                formatAbsoluteChange={formatCurrency}
                                color={gmv.trend && gmv.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        const ltv = extractValueAndTrend(revenueData.ltv);
                        const trendData = ltv.trendData ? extractTrendData(ltv.trendData) : undefined;
                        return (
                            <KPICard
                                title="LTV"
                                subtitle="Lifetime Value"
                                value={formatCurrency(ltv.value)}
                                trend={ltv.trend}
                                trendLabel={ltv.trendLabel}
                                trendData={trendData}
                                absoluteChange={ltv.absoluteChange}
                                formatAbsoluteChange={formatCurrency}
                                color={ltv.trend && ltv.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        const totalContributions = extractValueAndTrend(revenueData.totalContributions);
                        const trendData = totalContributions.trendData ? extractTrendData(totalContributions.trendData) : undefined;
                        return (
                            <KPICard
                                title="Total contributions"
                                value={formatCurrency(totalContributions.value)}
                                trend={totalContributions.trend}
                                trendLabel={totalContributions.trendLabel}
                                trendData={trendData}
                                absoluteChange={totalContributions.absoluteChange}
                                formatAbsoluteChange={formatCurrency}
                                color={totalContributions.trend && totalContributions.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    <KPICard
                        title="Avg. One-time contribution"
                        value={formatCurrency(revenueData.avgContributions.oneTime)}
                        trend={4.1}
                        trendLabel="since last week"
                        color="#4caf50"
                        loading={loading}
                    />
                    <KPICard
                        title="Avg. Monthly contribution"
                        value={formatCurrency(revenueData.avgContributions.monthly)}
                        trend={32.5}
                        trendLabel="since last week"
                        color="#4caf50"
                        loading={loading}
                    />
                    {(() => {
                        const totalPurchases = extractValueAndTrend(revenueData.totalPurchases);
                        const trendData = totalPurchases.trendData ? extractTrendData(totalPurchases.trendData) : undefined;
                        return (
                            <KPICard
                                title="Total Purchases"
                                value={formatCurrency(totalPurchases.value)}
                                trend={totalPurchases.trend}
                                trendLabel={totalPurchases.trendLabel}
                                trendData={trendData}
                                absoluteChange={totalPurchases.absoluteChange}
                                formatAbsoluteChange={formatCurrency}
                                color={totalPurchases.trend && totalPurchases.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    <KPICard
                        title="Avg. Physical purchase"
                        value={formatCurrency(revenueData.avgContributions.physical)}
                        trend={4.1}
                        trendLabel="since last week"
                        color="#4caf50"
                        loading={loading}
                    />
                    <KPICard
                        title="Avg. Digital purchase"
                        value={formatCurrency(revenueData.avgContributions.digital)}
                        trend={32.5}
                        trendLabel="since last week"
                        color="#4caf50"
                        loading={loading}
                    />
                </div>

                <div className="gmv-charts">
                    <div className="chart-card">
                        <div className="chart-kpi">
                            <span className="kpi-label">GMV per source</span>
                            <span className="kpi-value">{formatCurrency(extractValueAndTrend(revenueData.gmv).value)}</span>
                            {(() => {
                                const gmv = extractValueAndTrend(revenueData.gmv);
                                if (gmv.trend !== null) {
                                    return (
                                        <span className={`kpi-trend ${gmv.trend >= 0 ? 'positive' : 'negative'}`}>
                                            {gmv.trend >= 0 ? '+' : ''}{gmv.trend?.toFixed(1)}% {gmv.trendLabel}
                                        </span>
                                    );
                                }
                                return null;
                            })()}
                        </div>
                        <StackedBarChart
                            data={gmvBySourceData}
                            xAxisKey="date"
                            bars={[
                                { key: 'Physical', color: '#D3F2FF', name: 'Physical' },
                                { key: 'Digital', color: '#8CDCFF', name: 'Digital' },
                                { key: 'Recurring', color: '#E865C0', name: 'Recurring' },
                                { key: 'OneTime', color: '#FFE8F8', name: 'One-time' }
                            ]}
                            height={300}
                            formatTooltip={formatCurrency}
                            loading={loading}
                        />
                    </div>

                    <div className="chart-card">
                        <div className="chart-kpi">
                            <span className="kpi-label">GMV per category</span>
                            <span className="kpi-value">{formatCurrency(extractValueAndTrend(revenueData.gmv).value)}</span>
                            {(() => {
                                const gmv = extractValueAndTrend(revenueData.gmv);
                                if (gmv.trend !== null) {
                                    return (
                                        <span className={`kpi-trend ${gmv.trend >= 0 ? 'positive' : 'negative'}`}>
                                            {gmv.trend >= 0 ? '+' : ''}{gmv.trend?.toFixed(1)}% {gmv.trendLabel}
                                        </span>
                                    );
                                }
                                return null;
                            })()}
                        </div>
                        <BarChart
                            data={gmvByCategoryData}
                            xAxisKey="name"
                            dataKey="value"
                            color="#ec407a"
                            height={300}
                            formatTooltip={formatCurrency}
                            loading={loading}
                        />
                    </div>
                </div>
            </div>

            {/* Average Time Section */}
            <div className="dashboard-section">
                <div className="section-header">
                    <h2>Average Time</h2>
                </div>

                <div className="time-kpis-grid">
                    <KPICard
                        title="Avg. time to Contribute (rec.)"
                        value={formatTime(revenueData.avgTimes.contributeRecurring)}
                        color="#9c27b0"
                        loading={loading}
                    />
                    <KPICard
                        title="Avg. time to Contribute (once)"
                        value={formatTime(revenueData.avgTimes.contributeOnce)}
                        color="#9c27b0"
                        loading={loading}
                    />
                    <KPICard
                        title="Avg. time to Physical Purchase"
                        value={formatTime(revenueData.avgTimes.physicalPurchase)}
                        color="#9c27b0"
                        loading={loading}
                    />
                    <KPICard
                        title="Avg. time to Digital Purchase"
                        value={formatTime(revenueData.avgTimes.digitalPurchase)}
                        color="#9c27b0"
                        loading={loading}
                    />
                </div>
            </div>
        </div>
    );
};

export default Revenue;

